import AuthManager from "Common/AuthManager";
import ServiceWrapper from "Services/ServiceWrapper";
import AppSettings from "Settings/AppSettings";
import { TFilters } from "types/hooks/useTable";

const api_base_url = AppSettings.GMS_BACKEND_API_URL;

/**
 * SKU sync-gap diagnostics (US 12396).
 *
 * Backend contract implemented by task 13325 ("BE API for product sku-channel
 * mapping"): `GET /product/diagnostics/<category_id>`.
 *
 * The backend does NOT enumerate the channel-environment columns — the caller
 * passes the domain's channel_keys in as `channel_envs` and the backend returns,
 * per product, the subset of those keys it is SKU-mapped in (`synced_envs`).
 * Only products with at least one gap are returned. The column list is therefore
 * owned by the frontend (see `Pages/GMS/Products/Diagnostics`, which builds it
 * from the current domain's parent channels + environments).
 */

/** One channel-environment column in the diagnostics matrix. */
export interface TDiagnosticsEnvColumn {
  /** child channel_key — the concrete SKU-mapping target for that environment */
  channel_key: string;
  /** parent channel name, e.g. "elkay.com" */
  channel_name: string;
  /** environment name, e.g. "Prod" / "Stage" */
  environment_name: string;
  /** column label as shown, e.g. "elkay.com - Prod" */
  label: string;
  /** true for the user's current environment column (rendered first, highlighted) */
  is_current: boolean;
}

/** One product row as returned by the backend, before column expansion. */
interface TDiagnosticsProductRaw {
  gms_id: string;
  name: string;
  sku?: string;
  /** default JPEG image extracted from the product_image_detail blob */
  image_url?: string | null;
  /** channel_keys (a subset of the requested `channel_envs`) the product is mapped in */
  synced_envs?: string[];
}

/** One product row: mapping presence keyed by env column channel_key. */
export interface TDiagnosticsProduct extends TDiagnosticsProductRaw {
  /** channel_key -> true (mapped ✓). Absent key = gap (✗). */
  mapping: Record<string, boolean>;
  /** number of requested env columns where the product is missing (✗) */
  gap_count: number;
}

export interface TDiagnosticsResponse {
  products: TDiagnosticsProduct[];
  /** total products with gaps across ALL pages (drives the count info bar) */
  total_count: number;
}

export interface TDiagnosticsQuery {
  page?: number;
  limit?: number;
  /** sort direction on the sync-gap count; backend defaults to "desc" */
  sort_order?: "asc" | "desc";
}

/** One product's sync instruction, as accepted by `PUT /product/sync_skus`. */
export interface TSkuSyncItem {
  gms_id: string;
  sku: string;
  /** channel_keys to create the SKU mapping in; envs already synced are excluded by the caller */
  target_envs: string[];
}

export interface TSkuSyncResult {
  /** sku rows written to the SKU service (products x target envs) */
  synced_count: number;
  /** products whose redis cache entry was refreshed */
  updated_count: number;
}

export interface TDiagnosticsExportColumn {
  channel_key: string;
  label: string;
  is_current: boolean;
}

export interface TDiagnosticsExportPayload {
  columns: TDiagnosticsExportColumn[];
  domain_name: string;
  sort_order: "asc" | "desc";
}

export interface TDiagnosticsExportResult {
  url: string;
  fileName: string;
}

function generateFiltersFromObject(filters?: TFilters | null) {
  if (!filters) return "";
  return Object.keys(filters)
    .filter((key) => filters[key] !== undefined && filters[key] !== null && filters[key] !== "")
    .map(
      (key) =>
        key +
        "=" +
        (typeof filters[key] !== "object" ? filters[key] : filters[key].toString())
    )
    .join("&");
}

/**
 * Fetch sync-gap diagnostics for a category. Data loads on open and on each
 * pagination click — there is no separate Run/Refresh action.
 *
 * @param categoryId  category being diagnosed
 * @param channelEnvs every channel_key in the current domain — the columns the
 *                    backend evaluates each product against. Required: the
 *                    backend rejects an empty list with a 400.
 */
export async function getProductSyncDiagnostics(
  categoryId: string,
  channelEnvs: string[],
  query?: TDiagnosticsQuery
): Promise<TDiagnosticsResponse> {
  try {
    if (!categoryId) throw new Error("No category id specified for diagnostics");
    if (!channelEnvs?.length)
      throw new Error("No channel environments found for the current domain");

    const _query = generateFiltersFromObject({
      ...(query ?? {}),
      channel_envs: channelEnvs.join(","),
    } as TFilters);

    const res = await ServiceWrapper.doGet(
      `${api_base_url}product/diagnostics/${categoryId}${_query ? `?${_query}` : ""}`,
      null,
      null,
      AuthManager.getGmsToken()
    );

    if (res?.errorfound || !res.data?.data)
      throw new Error(
        "Diagnostics fetch failed : " +
          (res?.data?.message ? res.data.message : "Unexpected error")
      );

    const body = res.data.data;
    const rows: TDiagnosticsProductRaw[] = body?.products_details ?? [];

    return {
      total_count: body?.total_count ?? 0,
      products: rows.map((row) => {
        const synced = row.synced_envs ?? [];
        return {
          ...row,
          synced_envs: synced,
          mapping: synced.reduce<Record<string, boolean>>((acc, key) => {
            acc[key] = true;
            return acc;
          }, {}),
          gap_count: channelEnvs.length - synced.length,
        };
      }),
    };
  } catch (err: any) {
    console.error(err?.message, 4);
    return Promise.reject(new Error(err?.message));
  }
}

/**
 * Create SKU mappings for the given products in their target environments.
 *
 * The backend is the source of truth writer: it copies the env-agnostic SKU
 * attributes (parent product id, real sku) from an environment the product is
 * already synced to, writes one row per product/target env to the SKU service,
 * then refreshes the redis product cache the diagnostics view reads from.
 *
 * Products already present in a target env must be filtered out by the caller —
 * the backend upserts, so a target env that is already mapped would be rewritten.
 */
export async function syncSkusToEnvironments(
  items: TSkuSyncItem[]
): Promise<TSkuSyncResult> {
  try {
    if (!items?.length) throw new Error("No products to sync");

    const res = await ServiceWrapper.doPut(
      `${api_base_url}product/sync_skus`,
      items,
      null,
      AuthManager.getGmsToken()
    );

    if (res?.errorfound || !res.data?.done)
      throw new Error(
        "Sync failed : " +
          (res?.data?.message ? res.data.message : "Unexpected error")
      );

    return {
      synced_count: res.data?.data?.synced_count ?? 0,
      updated_count: res.data?.data?.updated_count ?? 0,
    };
  } catch (err: any) {
    console.error(err?.message, 4);
    return Promise.reject(new Error(err?.message));
  }
}

export async function exportProductSyncDiagnostics(
  categoryId: string,
  payload: TDiagnosticsExportPayload
): Promise<TDiagnosticsExportResult> {
  try {
    if (!categoryId) throw new Error("No category id specified for diagnostics export");
    if (!payload?.columns?.length)
      throw new Error("No channel environments found for the current domain");

    const res = await ServiceWrapper.doPost(
      `${api_base_url}product/diagnostics/${categoryId}/export`,
      payload,
      null,
      AuthManager.getGmsToken()
    );

    const url = res?.data?.data?.url;
    if (res?.errorfound || !res?.data?.done || !url)
      throw new Error(
        "Diagnostics export failed : " +
          (res?.data?.message ? res.data.message : "Unexpected error")
      );

    return { url, fileName: res.data.data.fileName ?? "" };
  } catch (err: any) {
    console.error(err?.message, 4);
    return Promise.reject(new Error(err?.message));
  }
}
